// src/components/Header/index.tsx

import React, { memo, useCallback, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  EllipsisOutlined,
  LoginOutlined,
  LogoutOutlined,
  UnorderedListOutlined,
  DatabaseOutlined,
  DollarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { MenuProps, Menu, Dropdown, Switch } from "antd";

import { GRAFANA_WORKSPACE_BASE_URL, MODEL_SANCTUM_BASE_URL } from "const";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

// import SupportMenu from "../SupportMenu";
import Button from "common/Button";
import { useLogin } from "hooks/useLogin";

import "./style.scss";
import { getSagemakerDomainByDisplayName } from "utils/utils";

const HOME_MENU_KEY = "list";

const Header: React.FC = () => {
  const msalInstance = useMsal();
  const { login, logout } = useLogin(msalInstance);

  const handleLogin = useCallback(() => login({ reloadPage: false }), [login]);

  const [switchOn, setSwitchOn] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const accountName: string | undefined = msalInstance.accounts[0]?.name;
  const accountMenuLabel = accountName
    ? `Open account menu for ${accountName}`
    : "Open account menu";
  const location = useLocation();

  const selectedDomainFromPath = useMemo(() => {
    const match = location.pathname.match(/^\/(?:explorer|space-details)\/([^/]+)/i);
    if (!match?.[1]) {
      return "";
    }

    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [location.pathname]);

  const isAnalyticsDomainSelected = selectedDomainFromPath.trim().toLowerCase() === "analytics";

  // The URL is the single source of truth for the highlighted nav item
  const selectedKeys = useMemo(
    () => (location.pathname === "/" ? [HOME_MENU_KEY] : []),
    [location.pathname],
  );

  const susLinkgen = useCallback(() => {
    const domainId = getSagemakerDomainByDisplayName("Common");
    return domainId
      ? `https://${domainId}.sagemaker.us-east-1.on.aws/projects`
      : "";
  }, []);

  // A single horizontal menu lets antd collapse overflowing links into a "..."
  // dropdown instead of letting them run into the right-hand controls.
  const navMenuItems = useMemo<MenuProps["items"]>(() => {
    const items: MenuProps["items"] = [
      {
        key: HOME_MENU_KEY,
        icon: <UnorderedListOutlined className="menu-icon" />,
        className: "menu-item",
        label: <Link to="/">HOME</Link>,
      },
      {
        key: "sagemaker-projects",
        icon: <DatabaseOutlined />,
        className: "menu-item",
        label: (
          <a href={susLinkgen()} target="_blank" rel="noopener noreferrer">
            Sagemaker Projects
          </a>
        ),
      },
      {
        key: "cost-dashboard",
        icon: <DollarOutlined />,
        className: "menu-item",
        label: (
          <a
            href={`${GRAFANA_WORKSPACE_BASE_URL}/login`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Cost Dashboard
          </a>
        ),
      },
    ];

    if (isAnalyticsDomainSelected) {
      items.push({
        key: "modelsanctum",
        icon: <DatabaseOutlined />,
        className: "menu-item",
        label: (
          <a href={MODEL_SANCTUM_BASE_URL} target="_blank" rel="noopener noreferrer">
            ModelSanctum
          </a>
        ),
      });
    }

    return items;
  }, [isAnalyticsDomainSelected, susLinkgen]);

  // Dropdown menu, including our V1 switch
  const accountMenu = useMemo(
    () => (
      <Menu
        items={[
          {
            key: "switch-version",
            className: "header-switch-version",
            label: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: 200,
                  padding: "4px 8px",
                }}
              >
                <Switch
                  size="small"
                  checked={switchOn}
                  onChange={(checked) => {
                    if (checked) {
                      // navigate in the same tab:
                      window.location.href =
                        "https://app.nexus-dev.ph-dse.int.bayer.com/";
                    }
                    // reset the switch back off
                    setSwitchOn(false);
                  }}
                />
              </div>
            ),
          },
          { type: "divider" },
          {
            key: "logout",
            className: "header-logout",
            label: (
              <div onClick={logout} className="header-logout-btn">
                <LogoutOutlined className="logout-icon" />
                Log out
              </div>
            ),
          },
        ]}
      />
    ),
    [logout, switchOn],
  );

  return (
    <header
      className={`header ${isAuthenticated ? "" : "header-auth"
        }`}
    >
      <div className="header-left">
        <Link to="/home" className="logo">
          PH DSE Nexus
        </Link>
        {isAuthenticated && (
          <Menu
            selectedKeys={selectedKeys}
            mode="horizontal"
            className="header-menu"
            overflowedIndicator={<EllipsisOutlined />}
            items={navMenuItems}
          />
        )}
      </div>

      <div className="header-right">
        {isAuthenticated ? (
          <>
            {/* <SupportMenu /> */}
            <Dropdown overlay={accountMenu} trigger={["click"]} arrow>
              <button
                type="button"
                className="header-account"
                title={accountName}
                aria-label={accountMenuLabel}
              >
                <UserOutlined className="header-account-icon" />
                <span className="header-account-name">{accountName ?? "Account"}</span>
              </button>
            </Dropdown>
          </>
        ) : (
          <Button
            icon={<LoginOutlined />}
            onClick={handleLogin}
            type="primary"
            size="large"
          >
            Login
          </Button>
        )}
      </div>
    </header>
  );
};

export default memo(Header);
